/**
 * Custom guided-tour tooltip (REQ D4-ONB-002 / D4-ONB-003).
 *
 * NO react-joyride default UI — a Tailwind card built on the project brand
 * tokens (navy / accent family via the shared ``--color-*`` CSS variables, so
 * it is theme-adaptive light/dark through the ``class`` strategy), Inter font,
 * 8px rounded corners and a subtle shadow. Controls: a "Step n of N" progress
 * indicator, Back / Next (Finish on the last step), Skip-to-next-section, End
 * tour, and an X (also Esc / overlay) to cancel. Every control is a ≥44px touch
 * target with an ARIA label; transitions are driven through the tour engine
 * (useTour) rather than joyride's button getters so the controlled-mode state
 * stays the single source of truth.
 */
import { X } from "lucide-react";
import type { TooltipRenderProps } from "react-joyride";
import { useTour } from "../../contexts/tour";
import { isHandoffStep } from "../../lib/tours/joyride";

const SECONDARY_BTN =
  "inline-flex min-h-[44px] items-center justify-center rounded-lg px-3 text-sm " +
  "font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)] " +
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent transition-colors";

export function TourTooltip({
  step,
  index,
  size,
  isLastStep,
  tooltipProps,
}: TooltipRenderProps) {
  const { back, next, skipSection, endTour, hasNextSection, currentStep, isHint } =
    useTour();
  const section = currentStep?.section;
  const showBack = index > 0;
  // A hand-off step (D9 WP24, #3551) advances when the user opens a record, so
  // it offers no Next and no section skip -- either would jump onto a record
  // page the user has not opened yet, which ends the tour.
  const handoff = currentStep ? isHandoffStep(currentStep) : false;

  // A one-off hint from the help assistant (D10-ACT-004, #3606): same card,
  // but no step counter, Back, section skip or End tour -- just the text and
  // "Got it". Its body is the assistant's plain text, rendered as a text node.
  if (isHint) {
    return (
      <div
        {...tooltipProps}
        data-testid="tour-hint"
        className="w-[min(92vw,22rem)] overflow-hidden rounded-[10px] border border-[var(--color-border)] bg-[var(--color-bg-card)] font-sans text-[var(--color-text-primary)] shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4">
          {step.title && (
            <h2 className="min-w-0 text-base font-semibold leading-snug">{step.title}</h2>
          )}
          <button
            type="button"
            onClick={() => endTour("cancelled")}
            aria-label="Close hint"
            className="-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="px-4 pb-3 pt-1 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {currentStep?.body ?? ""}
        </p>
        <div className="flex justify-end border-t border-[var(--color-border)] px-4 py-3">
          <button
            type="button"
            onClick={() => endTour("cancelled")}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-brand-accent px-4 text-sm font-semibold text-white hover:bg-brand-accent-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-card)] transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      {...tooltipProps}
      className="w-[min(92vw,22rem)] overflow-hidden rounded-[10px] border border-[var(--color-border)] bg-[var(--color-bg-card)] font-sans text-[var(--color-text-primary)] shadow-xl"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          {section && (
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-accent">
              {section}
            </p>
          )}
          {step.title && (
            <h2 className="mt-0.5 text-lg font-semibold leading-snug">
              {step.title}
            </h2>
          )}
        </div>
        <button
          type="button"
          onClick={() => endTour("cancelled")}
          aria-label="Close tour"
          className="-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 pb-3 pt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        {step.content}
      </div>

      {/* Primary controls: progress + Back / Next */}
      <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] px-4 py-3">
        <span
          className="text-xs font-medium text-[var(--color-text-muted)]"
          aria-live="polite"
        >
          Step {index + 1} of {size}
        </span>
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              type="button"
              onClick={back}
              aria-label="Previous step"
              className={SECONDARY_BTN}
            >
              Back
            </button>
          )}
          {handoff ? (
            <span className="text-xs font-medium text-[var(--color-text-muted)]">
              Waiting for you to open one
            </span>
          ) : (
            <button
              type="button"
              onClick={next}
              aria-label={isLastStep ? "Finish tour" : "Next step"}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-brand-accent px-4 text-sm font-semibold text-white hover:bg-brand-accent-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-card)] transition-colors"
            >
              {isLastStep ? "Finish" : "Next"}
            </button>
          )}
        </div>
      </div>

      {/* Secondary controls: skip-section + end tour */}
      <div className="flex items-center justify-between gap-2 px-2 pb-2">
        {hasNextSection && !handoff ? (
          <button
            type="button"
            onClick={skipSection}
            aria-label="Skip to the next section"
            className={SECONDARY_BTN}
          >
            Skip to next section
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => endTour("skipped")}
          aria-label="End the tour"
          className={SECONDARY_BTN}
        >
          End tour
        </button>
      </div>
    </div>
  );
}
